import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Bell, PartyPopper, Frown, CheckCheck, Banknote, XCircle, CheckCircle2, Award, Users, Gift } from "lucide-react";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/lib/afritask";

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const ICON_BY_TYPE = {
  selection_won: { icon: PartyPopper, tone: "text-emerald-600 bg-emerald-50" },
  selection_missed: { icon: Frown, tone: "text-neutral-500 bg-neutral-100" },
  submission_approved: { icon: CheckCircle2, tone: "text-emerald-600 bg-emerald-50" },
  submission_rejected: { icon: XCircle, tone: "text-red-600 bg-red-50" },
  revision_requested: { icon: Bell, tone: "text-amber-600 bg-amber-50" },
  withdrawal_approved: { icon: Banknote, tone: "text-amber-700 bg-amber-50" },
  withdrawal_paid: { icon: CheckCircle2, tone: "text-emerald-600 bg-emerald-50" },
  withdrawal_failed: { icon: XCircle, tone: "text-red-600 bg-red-50" },
  withdrawal_rejected: { icon: XCircle, tone: "text-red-600 bg-red-50" },
  badge_earned: { icon: Award, tone: "text-amber-600 bg-amber-50" },
  referral_signup: { icon: Users, tone: "text-amber-700 bg-amber-50" },
  referral_activated: { icon: Gift, tone: "text-emerald-600 bg-emerald-50" },
  welcome_bonus: { icon: Gift, tone: "text-amber-600 bg-amber-50" },
};

export default function NotificationsBell({ userReady }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const d = await listNotifications(5);
      setItems(d.notifications);
      setUnread(d.unread);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!userReady) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [userReady]);

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      await markNotificationRead(n.notification_id).catch(() => {});
    }
    load();
    navigate(`/app/messages?open=${n.notification_id}`);
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead().catch(() => {});
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-neutral-500"
          data-testid="topbar-bell"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute right-1 top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white"
              data-testid="bell-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 p-0"
        data-testid="bell-dropdown"
      >
        <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && (
            <button
              onClick={handleMarkAll}
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <ul className="max-h-80 divide-y divide-neutral-100 overflow-y-auto">
          {items.length === 0 && (
            <li className="px-4 py-8 text-center text-xs text-neutral-400">
              No notifications yet
            </li>
          )}
          {items.map((n) => {
            const meta = ICON_BY_TYPE[n.type] || {
              icon: Bell,
              tone: "text-neutral-500 bg-neutral-100",
            };
            const Icon = meta.icon;
            return (
              <li key={n.notification_id}>
                <button
                  onClick={() => handleClick(n)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-neutral-50 ${
                    n.read ? "" : "bg-amber-50/40"
                  }`}
                  data-testid={`notif-item-${n.notification_id}`}
                >
                  <span
                    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.tone}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-sm font-medium text-neutral-900">
                      {n.title}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-neutral-600">
                      {n.body}
                    </p>
                    <p className="mt-1 text-[10px] text-neutral-400">
                      {relTime(n.created_at)}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-neutral-100 p-2">
          <Link
            to="/app/messages"
            onClick={() => setOpen(false)}
            className="block rounded-md bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
            data-testid="see-all-notifications"
          >
            View all in Message Centre →
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
